import React, { useState, useEffect, useMemo } from 'react';
import {
  Wallet, Receipt, FileText, Plus, Trash2, Edit2, Download, ArrowUpRight, ArrowDownLeft, RefreshCw, X, FileCheck, FileType
} from 'lucide-react';
import { CaisseService, DepenseService, FactureService } from '../services/comptaService';
import { AuditService, AUDIT_ACTIONS } from '../services/auditService';
import { NotifyService } from '../services/notifyService';
import { formatNumber, formatDate, formatDateTime } from '../utils/helpers';
import {
  computeCaisse, computeDepensesStats, CATEGORIES_DEPENSES, MODES_PAIEMENT, computeFactureTotals, DEFAULT_TVA, toCsv, downloadText,
  computeTimbre
} from '../utils/finance';
import { generateFacturePdf } from '../utils/facturePdf';
import { TemplateService } from '../services/templateService';

const today = () => new Date().toISOString().slice(0, 10);

const Tabs = ({ tabs, active, onChange }) => (
  <div className="flex gap-1 bg-slate-100 p-1 rounded-xl overflow-x-auto no-scrollbar" role="tablist">
    {tabs.map((t) => (
      <button
        key={t.key}
        role="tab"
        aria-selected={active === t.key}
        onClick={() => onChange(t.key)}
        className={`flex items-center gap-2 px-3 sm:px-4 min-h-[40px] rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
          active === t.key ? 'bg-white text-brand-800 shadow-card' : 'text-slate-500 hover:text-slate-800'
        }`}
      >
        <t.icon className="h-4 w-4" />
        {t.label}
      </button>
    ))}
  </div>
);

// ---------------- Caisse ----------------
const CaisseTab = ({ cabinetId, projects, depenses, user, profile }) => {
  const [config, setConfig] = useState({ initialAmount: 0, resetAt: null });
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    CaisseService.getConfig(cabinetId).then((r) => setConfig(r.config));
  }, [cabinetId]);

  const caisse = useMemo(() => computeCaisse({ config, projects, depenses }), [config, projects, depenses]);

  const handleSave = async () => {
    if (amount === '' || Number.isNaN(parseFloat(amount))) {
      setError('Saisissez un montant');
      return;
    }
    if (!window.confirm('Définir ce montant comme nouveau solde de caisse ? Seuls les paiements et dépenses enregistrés à partir de maintenant seront comptés.')) return;
    const r = await CaisseService.setInitialAmount(cabinetId, amount, user?.uid);
    if (r.success) {
      const next = await CaisseService.getConfig(cabinetId);
      setConfig(next.config);
      setEditing(false);
      setError('');
      AuditService.log(AUDIT_ACTIONS.CAISSE_RESET, { entityType: 'caisse', label: `Montant initial ${formatNumber(amount)} DH` });
    } else {
      setError(r.error);
    }
  };

  const exportCsv = () => {
    const csv = toCsv(caisse.mouvements, [
      { label: 'Date', value: 'date' }, { label: 'Type', value: (m) => (m.type === 'entree' ? 'Entrée' : 'Sortie') },
      { label: 'Libellé', value: 'label' }, { label: 'Montant', value: 'montant' }, { label: 'Mode', value: 'modePaiement' }
    ]);
    downloadText(`caisse_${today()}.csv`, csv);
  };

  const positive = caisse.solde >= 0;
  return (
    <div className="space-y-4">
      <div className={`rounded-2xl p-5 sm:p-6 text-white ${positive ? 'bg-gradient-to-br from-emerald-600 to-emerald-700' : 'bg-gradient-to-br from-red-600 to-red-700'}`}>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-white/80">Solde de caisse</p>
            <p className="font-heading text-3xl sm:text-4xl font-bold tabular-nums mt-1">{formatNumber(caisse.solde)} DH</p>
            <p className="text-sm text-white/80 mt-2">
              Initial {formatNumber(caisse.initialAmount)} + Entrées {formatNumber(caisse.entrees)} − Sorties {formatNumber(caisse.sorties)}
              {caisse.resetAt && <span className="block sm:inline sm:ml-2 text-white/70">· depuis le {formatDateTime(caisse.resetAt)}</span>}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={exportCsv} className="btn bg-white/15 text-white hover:bg-white/25">
              <Download className="h-4 w-4" /> CSV
            </button>
            <button onClick={() => { setEditing(!editing); setAmount(String(caisse.initialAmount || '')); }} className="btn bg-white text-slate-800 hover:bg-slate-100">
              <RefreshCw className="h-4 w-4" /> Montant initial
            </button>
          </div>
        </div>
        {editing && (
          <div className="mt-4 bg-white/10 rounded-xl p-3 flex flex-col sm:flex-row gap-2">
            <input
              type="number" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="field !text-slate-900 flex-1" placeholder="Montant initial (DH)" aria-label="Montant initial"
            />
            <button onClick={handleSave} className="btn bg-white text-emerald-800 hover:bg-emerald-50">Enregistrer</button>
            <button onClick={() => setEditing(false)} className="btn text-white hover:bg-white/15">Annuler</button>
            {error && <p className="text-sm text-white sm:self-center">{error}</p>}
          </div>
        )}
      </div>

      <div className="card overflow-hidden">
        <div className="px-4 sm:px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Mouvements de caisse</h3>
          <span className="text-xs text-slate-500">{caisse.mouvements.length} mouvement(s)</span>
        </div>
        {caisse.mouvements.length === 0 ? (
          <p className="p-6 text-sm text-slate-500 text-center">
            Aucun mouvement. Cochez « En caisse » sur une avance ou « Depuis la caisse » sur une dépense.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {caisse.mouvements.map((m, i) => (
              <li key={i} className="flex items-center gap-3 px-4 sm:px-5 py-3">
                <span className={`p-2 rounded-lg ${m.type === 'entree' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                  {m.type === 'entree' ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{m.label}</p>
                  <p className="text-xs text-slate-500">{formatDate(m.date)}{m.modePaiement ? ` · ${m.modePaiement}` : ''}</p>
                </div>
                <span className={`text-sm font-semibold tabular-nums ${m.type === 'entree' ? 'text-emerald-700' : 'text-red-600'}`}>
                  {m.type === 'entree' ? '+' : '−'}{formatNumber(m.montant)} DH
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ---------------- Dépenses ----------------
const emptyDepense = () => ({ montant: '', date: today(), categorie: CATEGORIES_DEPENSES[0], description: '', depuisCaisse: true, modePaiement: 'espece', projectId: '' });

const DepensesTab = ({ cabinetId, depenses, projects, user }) => {
  const [form, setForm] = useState(emptyDepense());
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [filterMois, setFilterMois] = useState('');
  const stats = useMemo(() => computeDepensesStats(depenses), [depenses]);

  const filtered = useMemo(() => filterMois ? depenses.filter((d) => (d.date || '').startsWith(filterMois)) : depenses, [depenses, filterMois]);
  const mois = useMemo(() => Object.keys(stats.parMois).sort().reverse(), [stats]);

  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  const submit = async () => {
    setError('');
    if (!(parseFloat(form.montant) > 0)) { setError('Montant invalide'); return; }
    const actor = { uid: user?.uid, name: user?.name };
    const r = editingId ? await DepenseService.update(editingId, form) : await DepenseService.add(cabinetId, form, actor);
    if (!r.success) { setError(r.error); return; }
    AuditService.log(editingId ? AUDIT_ACTIONS.DEPENSE_UPDATE : AUDIT_ACTIONS.DEPENSE_CREATE, {
      entityType: 'depense', entityId: editingId || r.id, label: `${form.categorie} · ${formatNumber(form.montant)} DH`
    });
    if (!editingId) NotifyService.send('depense', { montant: parseFloat(form.montant), categorie: form.categorie, description: form.description, par: user?.name });
    setForm(emptyDepense());
    setEditingId(null);
    setShowForm(false);
  };

  const edit = (d) => {
    setForm({ montant: d.montant, date: d.date, categorie: d.categorie, description: d.description || '', depuisCaisse: d.depuisCaisse === true, modePaiement: d.modePaiement || 'espece', projectId: d.projectId || '' });
    setEditingId(d.id);
    setShowForm(true);
  };

  const remove = async (d) => {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    const r = await DepenseService.remove(d.id);
    if (r.success) AuditService.log(AUDIT_ACTIONS.DEPENSE_DELETE, { entityType: 'depense', entityId: d.id, label: `${d.categorie} · ${formatNumber(d.montant)} DH` });
  };

  const exportCsv = () => {
    downloadText(`depenses_${today()}.csv`, toCsv(filtered, [
      { label: 'Date', value: 'date' }, { label: 'Catégorie', value: 'categorie' }, { label: 'Description', value: 'description' },
      { label: 'Montant', value: 'montant' }, { label: 'Mode', value: 'modePaiement' }, { label: 'Depuis caisse', value: (d) => (d.depuisCaisse ? 'oui' : 'non') }
    ]));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="card p-4"><p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Total dépenses</p><p className="kpi-value mt-1 text-xl sm:text-2xl">{formatNumber(stats.total)} DH</p></div>
        <div className="card p-4"><p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Ce mois</p><p className="kpi-value mt-1 text-xl sm:text-2xl">{formatNumber(stats.parMois[today().slice(0, 7)] || 0)} DH</p></div>
        <div className="card p-4 col-span-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">Par catégorie</p>
          <div className="space-y-1.5 max-h-24 overflow-y-auto custom-scrollbar pr-1">
            {Object.entries(stats.parCategorie).sort((a, b) => b[1] - a[1]).map(([cat, v]) => (
              <div key={cat} className="flex items-center gap-2 text-xs">
                <span className="w-32 truncate text-slate-600">{cat}</span>
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-brand-500 rounded-full" style={{ width: `${stats.total ? (v / stats.total) * 100 : 0}%` }} /></div>
                <span className="tabular-nums text-slate-700 w-24 text-right">{formatNumber(v)} DH</span>
              </div>
            ))}
            {Object.keys(stats.parCategorie).length === 0 && <p className="text-xs text-slate-400">Aucune dépense</p>}
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <select value={filterMois} onChange={(e) => setFilterMois(e.target.value)} className="field sm:w-48" aria-label="Filtrer par mois">
          <option value="">Tous les mois</option>
          {mois.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <div className="flex gap-2 sm:ml-auto">
          <button onClick={exportCsv} className="btn-secondary"><Download className="h-4 w-4" /> CSV</button>
          <button onClick={() => { setForm(emptyDepense()); setEditingId(null); setShowForm(true); }} className="btn-primary flex-1 sm:flex-none"><Plus className="h-4 w-4" /> Dépense</button>
        </div>
      </div>

      {showForm && (
        <div className="card p-4 sm:p-5 space-y-3 border-brand-200">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">{editingId ? 'Modifier la dépense' : 'Nouvelle dépense'}</h3>
            <button onClick={() => setShowForm(false)} className="icon-btn" aria-label="Fermer"><X className="h-5 w-5" /></button>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div><label className="label">Montant (DH) *</label><input type="number" inputMode="decimal" min="0" value={form.montant} onChange={(e) => set('montant', e.target.value)} className="field" /></div>
            <div><label className="label">Date</label><input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} className="field" /></div>
            <div><label className="label">Catégorie</label>
              <select value={form.categorie} onChange={(e) => set('categorie', e.target.value)} className="field">{CATEGORIES_DEPENSES.map((c) => <option key={c}>{c}</option>)}</select></div>
            <div><label className="label">Mode de paiement</label>
              <select value={form.modePaiement} onChange={(e) => set('modePaiement', e.target.value)} className="field">{MODES_PAIEMENT.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}</select></div>
            <div><label className="label">Dossier (optionnel)</label>
              <select value={form.projectId} onChange={(e) => set('projectId', e.target.value)} className="field">
                <option value="">—</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.refGlobal} · {p.client}</option>)}
              </select></div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 min-h-[44px] cursor-pointer">
                <input type="checkbox" checked={form.depuisCaisse} onChange={(e) => set('depuisCaisse', e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-brand-700 focus:ring-brand-500" />
                <span className="text-sm text-slate-700">Payée depuis la caisse</span>
              </label>
            </div>
            <div className="sm:col-span-2 lg:col-span-3"><label className="label">Description</label><input type="text" value={form.description} onChange={(e) => set('description', e.target.value)} className="field" placeholder="Ex : loyer du bureau, août" /></div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="btn-secondary">Annuler</button>
            <button onClick={submit} className="btn-primary">{editingId ? 'Enregistrer' : 'Ajouter'}</button>
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        {filtered.length === 0 ? (
          <p className="p-6 text-sm text-slate-500 text-center">Aucune dépense enregistrée</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((d) => (
              <li key={d.id} className="flex items-center gap-3 px-4 sm:px-5 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{d.categorie}{d.description ? ` · ${d.description}` : ''}</p>
                  <p className="text-xs text-slate-500">{formatDate(d.date)} · {MODES_PAIEMENT.find((m) => m.value === d.modePaiement)?.label || d.modePaiement}{d.depuisCaisse ? ' · caisse' : ''}{d.createdByName ? ` · ${d.createdByName}` : ''}</p>
                </div>
                <span className="text-sm font-semibold tabular-nums text-slate-900">{formatNumber(d.montant)} DH</span>
                <button onClick={() => edit(d)} className="icon-btn" aria-label="Modifier"><Edit2 className="h-4 w-4" /></button>
                <button onClick={() => remove(d)} className="icon-btn hover:text-red-600 hover:bg-red-50" aria-label="Supprimer"><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ---------------- Devis / Factures ----------------
const STATUTS_DOC = {
  devis: [['brouillon', 'Brouillon'], ['envoye', 'Envoyé'], ['accepte', 'Accepté'], ['refuse', 'Refusé']],
  facture: [['brouillon', 'Brouillon'], ['envoyee', 'Envoyée'], ['payee', 'Payée'], ['annulee', 'Annulée']]
};
const STATUT_STYLE = { brouillon: 'bg-slate-100 text-slate-600', envoye: 'bg-brand-50 text-brand-700', envoyee: 'bg-brand-50 text-brand-700', accepte: 'bg-emerald-50 text-emerald-700', payee: 'bg-emerald-50 text-emerald-700', refuse: 'bg-red-50 text-red-700', annulee: 'bg-red-50 text-red-700' };
const emptyLigne = () => ({ designation: '', unite: 'F', quantite: 1, prixUnitaire: '' });
const emptyFacture = (tva) => ({ type: 'devis', date: today(), projectId: '', clientNom: '', clientAdresse: '', clientIdentifiant: '', objet: '', lignes: [emptyLigne()], tauxTVA: tva, notes: '', espece: false });

const FacturesTab = ({ cabinetId, factures, projects, user, profile }) => {
  const defaultTva = profile?.tauxTVA ?? DEFAULT_TVA;
  const [form, setForm] = useState(emptyFacture(defaultTva));
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState({});
  useEffect(() => { TemplateService.getAll(cabinetId).then(setTemplates); }, [cabinetId]);
  const totals = useMemo(() => {
    const t = computeFactureTotals(form.lignes, form.tauxTVA);
    return { ...t, ...computeTimbre(t.montantTTC, form.type === 'facture' && form.espece) };
  }, [form.lignes, form.tauxTVA, form.type, form.espece]);
  const renderWord = (f) => {
    try { TemplateService.render(templates[f.type], f, profile); }
    catch (e) { window.alert(`Modèle ${f.type} invalide : ${e.message}`); }
  };
  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));
  const setLigne = (i, f, v) => setForm((p) => ({ ...p, lignes: p.lignes.map((l, j) => (j === i ? { ...l, [f]: v } : l)) }));

  const pickProject = (projectId) => {
    const p = projects.find((x) => x.id === projectId);
    set('projectId', projectId);
    if (p) {
      const c = (p.clients || [])[0];
      setForm((prev) => ({
        ...prev,
        clientNom: p.client || prev.clientNom,
        clientIdentifiant: c ? (c.type === 'societe' ? (c.ice ? `ICE ${c.ice}` : '') : (c.cin ? `CIN ${c.cin}` : '')) : prev.clientIdentifiant,
        objet: prev.objet || `Honoraires d'architecte — ${p.natureProjet || 'projet'} · ${p.commune || ''}`.trim(),
        lignes: prev.lignes.length === 1 && !prev.lignes[0].designation && p.montantTotal
          ? [{ designation: `Honoraires — ${p.natureProjet || 'projet'} (${p.refGlobal})`, unite: 'F', quantite: 1, prixUnitaire: p.montantTotal }]
          : prev.lignes
      }));
    }
  };

  const submit = async () => {
    setError('');
    if (!form.clientNom.trim()) { setError('Nom du client requis'); return; }
    if (!form.lignes.some((l) => l.designation && parseFloat(l.prixUnitaire) > 0)) { setError('Ajoutez au moins une ligne avec un prix'); return; }
    const p = projects.find((x) => x.id === form.projectId);
    const data = { ...form, refGlobal: p?.refGlobal || '', ...totals, espece: form.type === 'facture' && form.espece === true, statut: form.statut || 'brouillon' };
    const actor = { uid: user?.uid, name: user?.name };
    const r = editingId ? await FactureService.update(editingId, data) : await FactureService.create(cabinetId, data, actor);
    if (!r.success) { setError(r.error); return; }
    AuditService.log(editingId ? AUDIT_ACTIONS.FACTURE_UPDATE : AUDIT_ACTIONS.FACTURE_CREATE, {
      entityType: form.type, entityId: editingId || r.id, label: `${form.type} ${r.numero || ''} · ${form.clientNom} · ${formatNumber(totals.montantTTC)} DH TTC`
    });
    setShowForm(false);
    setEditingId(null);
    setForm(emptyFacture(defaultTva));
  };

  const edit = (f) => {
    setForm({ type: f.type, date: f.date, projectId: f.projectId || '', clientNom: f.clientNom, clientAdresse: f.clientAdresse || '', clientIdentifiant: f.clientIdentifiant || '', objet: f.objet || '', lignes: f.lignes?.length ? f.lignes.map((l) => ({ unite: 'F', ...l })) : [emptyLigne()], tauxTVA: f.tauxTVA ?? defaultTva, notes: f.notes || '', statut: f.statut, espece: f.espece === true });
    setEditingId(f.id);
    setShowForm(true);
  };

  const remove = async (f) => {
    if (!window.confirm(`Supprimer ${f.type} ${f.numero} ?`)) return;
    const r = await FactureService.remove(f.id);
    if (r.success) AuditService.log(AUDIT_ACTIONS.FACTURE_DELETE, { entityType: f.type, entityId: f.id, label: `${f.type} ${f.numero}` });
  };

  const setStatut = async (f, statut) => {
    await FactureService.update(f.id, { statut });
    AuditService.log(AUDIT_ACTIONS.FACTURE_UPDATE, { entityType: f.type, entityId: f.id, label: `${f.type} ${f.numero} → ${statut}` });
  };

  const convert = async (f) => {
    if (!window.confirm(`Créer une facture à partir du devis ${f.numero} ?`)) return;
    const r = await FactureService.convertToFacture(cabinetId, f, { uid: user?.uid, name: user?.name });
    if (r.success) AuditService.log(AUDIT_ACTIONS.FACTURE_CREATE, { entityType: 'facture', entityId: r.id, label: `facture ${r.numero} (depuis devis ${f.numero})` });
  };

  const list = filterType ? factures.filter((f) => f.type === filterType) : factures;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
          {[['', 'Tous'], ['devis', 'Devis'], ['facture', 'Factures']].map(([v, l]) => (
            <button key={v} onClick={() => setFilterType(v)} className={`px-3 min-h-[36px] rounded-lg text-sm font-medium ${filterType === v ? 'bg-white text-brand-800 shadow-card' : 'text-slate-500'}`}>{l}</button>
          ))}
        </div>
        <button onClick={() => { setForm(emptyFacture(defaultTva)); setEditingId(null); setShowForm(true); }} className="btn-primary sm:ml-auto"><Plus className="h-4 w-4" /> Nouveau devis / facture</button>
      </div>
      <p className="text-xs text-slate-500 -mt-2">
        Modèles Word : devis {templates.devis ? <span className="text-emerald-700 font-medium">{templates.devis.name}</span> : <span className="text-slate-400">PDF intégré</span>}
        {' · '}facture {templates.facture ? <span className="text-emerald-700 font-medium">{templates.facture.name}</span> : <span className="text-slate-400">PDF intégré</span>}
        {' — '}déposez vos modèles dans <strong>Profil cabinet → Modèles</strong>.
      </p>

      {showForm && (
        <div className="card p-4 sm:p-5 space-y-4 border-brand-200">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">{editingId ? 'Modifier' : 'Nouveau document'}</h3>
            <button onClick={() => setShowForm(false)} className="icon-btn" aria-label="Fermer"><X className="h-5 w-5" /></button>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div><label className="label">Type</label>
              <select value={form.type} onChange={(e) => set('type', e.target.value)} disabled={!!editingId} className="field"><option value="devis">Devis</option><option value="facture">Facture</option></select></div>
            <div><label className="label">Date</label><input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} className="field" /></div>
            <div className="sm:col-span-2"><label className="label">Dossier lié (pré-remplit le client)</label>
              <select value={form.projectId} onChange={(e) => pickProject(e.target.value)} className="field"><option value="">—</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.refGlobal} · {p.client}</option>)}</select></div>
            <div className="sm:col-span-2"><label className="label">Client *</label><input type="text" value={form.clientNom} onChange={(e) => set('clientNom', e.target.value)} className="field" /></div>
            <div><label className="label">CIN / ICE</label><input type="text" value={form.clientIdentifiant} onChange={(e) => set('clientIdentifiant', e.target.value)} className="field" placeholder="ICE 00123…" /></div>
            <div><label className="label">Adresse</label><input type="text" value={form.clientAdresse} onChange={(e) => set('clientAdresse', e.target.value)} className="field" /></div>
            <div className="sm:col-span-2 lg:col-span-4"><label className="label">Objet</label><input type="text" value={form.objet} onChange={(e) => set('objet', e.target.value)} className="field" /></div>
          </div>

          <div className="space-y-2">
            <div className="hidden sm:grid grid-cols-12 gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 px-1">
              <span className="col-span-5">Désignation</span><span className="col-span-1">Unité</span><span className="col-span-2 text-right">Qté</span><span className="col-span-3 text-right">P.U. HT</span><span className="col-span-1" />
            </div>
            {form.lignes.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2">
                <input type="text" value={l.designation} onChange={(e) => setLigne(i, 'designation', e.target.value)} className="field col-span-12 sm:col-span-5" placeholder="Désignation" />
                <input type="text" value={l.unite ?? 'F'} onChange={(e) => setLigne(i, 'unite', e.target.value)} className="field col-span-3 sm:col-span-1" placeholder="U" title="Unité (F = forfait, U, m², ml…)" />
                <input type="number" inputMode="decimal" min="0" value={l.quantite} onChange={(e) => setLigne(i, 'quantite', e.target.value)} className="field col-span-3 sm:col-span-2 text-right" placeholder="Qté" />
                <input type="number" inputMode="decimal" min="0" value={l.prixUnitaire} onChange={(e) => setLigne(i, 'prixUnitaire', e.target.value)} className="field col-span-4 sm:col-span-3 text-right" placeholder="Prix unitaire HT" />
                <button onClick={() => setForm((p) => ({ ...p, lignes: p.lignes.length > 1 ? p.lignes.filter((_, j) => j !== i) : [emptyLigne()] }))} className="icon-btn col-span-2 sm:col-span-1 justify-self-end hover:text-red-600" aria-label="Retirer la ligne"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <button onClick={() => setForm((p) => ({ ...p, lignes: [...p.lignes, emptyLigne()] }))} className="btn-ghost text-brand-700"><Plus className="h-4 w-4" /> Ajouter une ligne</button>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
            <div className="flex gap-3 items-end">
              <div><label className="label">TVA (%)</label><input type="number" inputMode="decimal" min="0" value={form.tauxTVA} onChange={(e) => set('tauxTVA', e.target.value)} className="field w-24" /></div>
              <div className="flex-1"><label className="label">Notes / conditions</label><input type="text" value={form.notes} onChange={(e) => set('notes', e.target.value)} className="field" placeholder="Validité, modalités de paiement…" /></div>
              {form.type === 'facture' && (
                <label className="flex items-center gap-2 min-h-[44px] cursor-pointer whitespace-nowrap">
                  <input type="checkbox" checked={form.espece === true} onChange={(e) => set('espece', e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
                  <span className="text-sm text-slate-700">Payée en espèces <span className="text-slate-400">(timbre 0,25 %)</span></span>
                </label>
              )}
            </div>
            <div className="text-sm text-right tabular-nums space-y-0.5 bg-slate-50 rounded-xl px-4 py-2">
              <p className="text-slate-500">HT <span className="text-slate-800 font-medium ml-2">{formatNumber(totals.montantHT)} DH</span></p>
              <p className="text-slate-500">TVA <span className="text-slate-800 font-medium ml-2">{formatNumber(totals.montantTVA)} DH</span></p>
              <p className="font-heading font-bold text-slate-900 text-base">TTC {formatNumber(totals.montantTTC)} DH</p>
              {totals.fraisTimbre > 0 && (
                <>
                  <p className="text-slate-500">Frais du timbre <span className="text-slate-800 font-medium ml-2">{formatNumber(totals.fraisTimbre)} DH</span></p>
                  <p className="font-heading font-bold text-brand-700 text-base">À payer {formatNumber(totals.totalFinal)} DH</p>
                </>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="btn-secondary">Annuler</button>
            <button onClick={submit} className="btn-primary">{editingId ? 'Enregistrer' : 'Créer'}</button>
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        {list.length === 0 ? (
          <p className="p-6 text-sm text-slate-500 text-center">Aucun document</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {list.map((f) => (
              <li key={f.id} className="px-4 sm:px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[11px] font-semibold uppercase px-2 py-0.5 rounded-full ${f.type === 'devis' ? 'bg-brand-50 text-brand-700' : 'bg-violet-50 text-violet-700'}`}>{f.type}</span>
                    <span className="font-semibold text-slate-900">{f.numero}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUT_STYLE[f.statut] || STATUT_STYLE.brouillon}`}>{(STATUTS_DOC[f.type] || []).find(([v]) => v === f.statut)?.[1] || f.statut}</span>
                  </div>
                  <p className="text-sm text-slate-700 truncate mt-0.5">{f.clientNom}{f.objet ? ` — ${f.objet}` : ''}</p>
                  <p className="text-xs text-slate-500">{formatDate(f.date)}{f.refGlobal ? ` · dossier ${f.refGlobal}` : ''}{f.devisId ? ' · issue d\'un devis' : ''}</p>
                </div>
                <span className="font-semibold tabular-nums text-slate-900 sm:text-right">{formatNumber(f.totalFinal || f.montantTTC)} DH <span className="text-xs text-slate-400 font-normal">{f.fraisTimbre ? 'TTC + timbre' : 'TTC'}</span></span>
                <div className="flex items-center gap-1 flex-wrap">
                  <select value={f.statut} onChange={(e) => setStatut(f, e.target.value)} className="field !min-h-[36px] !py-1 !text-xs w-auto" aria-label="Statut">
                    {(STATUTS_DOC[f.type] || []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  {templates[f.type] ? (
                    <>
                      <button onClick={() => renderWord(f)} className="btn-primary !min-h-[36px] px-3 text-xs" title={`Générer avec votre modèle Word (${templates[f.type].name})`} aria-label="Word">
                        <FileType className="h-4 w-4" /> Word
                      </button>
                      <button onClick={() => generateFacturePdf(f, profile)} className="icon-btn" title="PDF intégré (sans votre modèle)" aria-label="PDF"><Download className="h-4 w-4" /></button>
                    </>
                  ) : (
                    <button onClick={() => generateFacturePdf(f, profile)} className="btn-secondary !min-h-[36px] px-3 text-xs" title="PDF intégré — déposez votre modèle Word dans Profil → Modèles" aria-label="PDF">
                      <Download className="h-4 w-4" /> PDF
                    </button>
                  )}
                  {f.type === 'devis' && !f.factureId && <button onClick={() => convert(f)} className="icon-btn hover:text-emerald-700" title="Convertir en facture" aria-label="Convertir en facture"><FileCheck className="h-4 w-4" /></button>}
                  <button onClick={() => edit(f)} className="icon-btn" aria-label="Modifier"><Edit2 className="h-4 w-4" /></button>
                  <button onClick={() => remove(f)} className="icon-btn hover:text-red-600 hover:bg-red-50" aria-label="Supprimer"><Trash2 className="h-4 w-4" /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ---------------- Vue Comptabilité ----------------
const Comptabilite = ({ cabinetId, projects, user, profile }) => {
  const [tab, setTab] = useState('caisse');
  const [depenses, setDepenses] = useState([]);
  const [factures, setFactures] = useState([]);

  useEffect(() => {
    if (!cabinetId) return undefined;
    const u1 = DepenseService.subscribe(cabinetId, setDepenses);
    const u2 = FactureService.subscribe(cabinetId, setFactures);
    return () => { u1(); u2(); };
  }, [cabinetId]);

  const tabs = [
    { key: 'caisse', label: 'Caisse', icon: Wallet },
    { key: 'depenses', label: 'Dépenses', icon: Receipt },
    { key: 'factures', label: 'Devis & Factures', icon: FileText }
  ];

  return (
    <div className="space-y-4">
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      {tab === 'caisse' && <CaisseTab cabinetId={cabinetId} projects={projects} depenses={depenses} user={user} profile={profile} />}
      {tab === 'depenses' && <DepensesTab cabinetId={cabinetId} depenses={depenses} projects={projects} user={user} />}
      {tab === 'factures' && <FacturesTab cabinetId={cabinetId} factures={factures} projects={projects} user={user} profile={profile} />}
    </div>
  );
};

export default Comptabilite;
